import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useTenant } from '../../../hooks/useTenant';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { NeoModal, NeoInput, NeoButton, NeoAlert } from '../../../components/Common';

/**
 * KioskLinksModal
 * Modal e container de links de Autoatendimento do Totem.
 * Suporta o modo inline (renderizado dentro de abas) e modal convencional.
 */
const KioskLinksModal = ({ isOpen, toggle, inline = false }) => {
    const { idTenant, tenantSlug } = useTenant();

    const [kioskKey, setKioskKey] = useState(null);
    const [loadingKey, setLoadingKey] = useState(false);

    useEffect(() => {
        if (isOpen && idTenant) {
            setLoadingKey(true);
            getDoc(doc(db, "tenants", idTenant))
                .then(async (docSnap) => {
                    if (docSnap.exists()) {
                        const existingToken = docSnap.data().kioskToken;
                        if (existingToken) {
                            setKioskKey(existingToken);
                        } else {
                            // Auto-gera o token na primeira vez
                            const array = new Uint8Array(8);
                            window.crypto.getRandomValues(array);
                            const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
                            await updateDoc(doc(db, "tenants", idTenant), { kioskToken: token });
                            setKioskKey(token);
                        }
                    }
                })
                .catch(err => console.error("Error fetching tenant info", err))
                .finally(() => setLoadingKey(false));
        }
    }, [isOpen, idTenant]);

    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url);
        toast.info("Link copiado para a área de transferência!");
    };

    // Base URL da aplicação (ex: https://neoapp.com.br)
    const baseUrl = window.location.origin;

    const tvUrl = kioskKey && tenantSlug ? `${baseUrl}/totem/tv/${tenantSlug}?key=${kioskKey}` : "";
    const printUrl = kioskKey && tenantSlug ? `${baseUrl}/totem/print/${tenantSlug}?key=${kioskKey}` : "";
    const autoUrl = kioskKey && tenantSlug ? `${baseUrl}/totem/auto/${tenantSlug}?key=${kioskKey}` : "";

    const content = (
        <div className={inline ? "" : "pt-1"}>
            {loadingKey ? (
                <div className="text-center py-5" style={{ fontFamily: "Outfit", color: "#482712" }}>
                    <i className="mdi mdi-loading mdi-spin me-2 font-size-24 text-accent" />
                    <p className="mt-2 fw-bold font-size-14 text-muted">Preparando links...</p>
                </div>
            ) : (
                <div className="d-flex flex-column gap-4 animate__animated animate__fadeIn">
                    <NeoAlert 
                        color="info"
                        title="Acesso sem Login"
                    >
                        Estes links permitem que seus tablets e TVs funcionem como totens <strong className="text-accent">sem precisar de login</strong>. Guarde-os em segurança.
                    </NeoAlert>

                    {/* Totem TV (Mural) */}
                    <div 
                        className="p-3 shadow-sm bg-white"
                        style={{
                            borderRadius: "12px",
                            border: "1.5px solid rgba(72, 39, 18, 0.12)",
                            transition: "all 0.2s"
                        }}
                    >
                        <div className="d-flex align-items-center mb-3">
                            <div 
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-3"
                                style={{
                                    width: "38px",
                                    height: "38px",
                                    background: "rgba(226, 102, 28, 0.08)",
                                    border: "1px solid rgba(226, 102, 28, 0.20)"
                                }}
                            >
                                <i className="mdi mdi-television font-size-20" style={{ color: "#e2661c" }}></i>
                            </div>
                            <div>
                                <h6 className="mb-0 text-dark fw-bold font-size-14" style={{ fontFamily: "Outfit", color: "#482712" }}>Totem TV (Mural de Mídias e Aulas)</h6>
                                <p className="mb-0 font-size-11 text-muted" style={{ fontFamily: "Outfit" }}>Para as telas grandes penduradas na parede.</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <NeoInput 
                                    type="text" 
                                    value={tvUrl} 
                                    readOnly={true}
                                    flat={true}
                                />
                            </div>
                            <NeoButton 
                                variant="outline-chocolate" 
                                icon="mdi-content-copy"
                                onClick={() => copyToClipboard(tvUrl)}
                            >
                                Copiar
                            </NeoButton>
                        </div>
                    </div>

                    {/* Totem Autoatendimento (Gamificação Completa) */}
                    <div 
                        className="p-3 shadow-sm bg-white"
                        style={{
                            borderRadius: "12px",
                            border: "1.5px solid rgba(72, 39, 18, 0.12)",
                            transition: "all 0.2s"
                        }}
                    >
                        <div className="d-flex align-items-center mb-3">
                            <div 
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-3"
                                style={{
                                    width: "38px",
                                    height: "38px",
                                    background: "rgba(120, 75, 34, 0.08)",
                                    border: "1px solid rgba(120, 75, 34, 0.20)"
                                }}
                            >
                                <i className="mdi mdi-account-group font-size-20" style={{ color: "#784b22" }}></i>
                            </div>
                            <div>
                                <h6 className="mb-0 text-dark fw-bold font-size-14" style={{ fontFamily: "Outfit", color: "#482712" }}>Totem de Autoatendimento (Gamificação)</h6>
                                <p className="mb-0 font-size-11 text-muted" style={{ fontFamily: "Outfit" }}>Para totens interativos onde os alunos acessam ranking e ficha completa.</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <NeoInput 
                                    type="text" 
                                    value={autoUrl} 
                                    readOnly={true}
                                    flat={true}
                                />
                            </div>
                            <NeoButton 
                                variant="outline-chocolate" 
                                icon="mdi-content-copy"
                                onClick={() => copyToClipboard(autoUrl)}
                            >
                                Copiar
                            </NeoButton>
                        </div>
                    </div>

                    {/* Totem de Impressão Expressa */}
                    <div 
                        className="p-3 shadow-sm bg-white"
                        style={{
                            borderRadius: "12px",
                            border: "1.5px solid rgba(72, 39, 18, 0.12)",
                            transition: "all 0.2s"
                        }}
                    >
                        <div className="d-flex align-items-center mb-3">
                            <div 
                                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-3"
                                style={{
                                    width: "38px",
                                    height: "38px",
                                    background: "rgba(75, 161, 88, 0.08)",
                                    border: "1px solid rgba(75, 161, 88, 0.20)"
                                }}
                            >
                                <i className="mdi mdi-printer font-size-20" style={{ color: "#4ba158" }}></i>
                            </div>
                            <div>
                                <h6 className="mb-0 text-dark fw-bold font-size-14" style={{ fontFamily: "Outfit", color: "#482712" }}>Totem de Impressão (Ficha de Treino)</h6>
                                <p className="mb-0 font-size-11 text-muted" style={{ fontFamily: "Outfit" }}>Para tablets ou totens touch-screen com impressora térmica.</p>
                            </div>
                        </div>
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <NeoInput 
                                    type="text" 
                                    value={printUrl} 
                                    readOnly={true}
                                    flat={true}
                                />
                            </div>
                            <NeoButton 
                                variant="outline-chocolate" 
                                icon="mdi-content-copy"
                                onClick={() => copyToClipboard(printUrl)}
                            >
                                Copiar
                            </NeoButton>
                        </div>
                    </div>

                </div>
            )}
        </div>
    );

    if (inline) {
        return content;
    }

    return (
        <NeoModal 
            isOpen={isOpen} 
            toggle={toggle} 
            size="lg" 
            title="Links Públicos do Totem"
            subtitle="Configure os acessos dos seus dispositivos de autoatendimento"
            headerIcon="mdi-link-variant"
        >
            {content}
        </NeoModal>
    );
};

KioskLinksModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    inline: PropTypes.bool
};

export default KioskLinksModal;
